/**
 * Project-level role → permission matrix. Shared by the API (enforcement) and the web app (what to show).
 * Tenant isolation and eyes-only visibility are enforced separately, in Postgres RLS.
 */
export const PROJECT_ROLES = ['owner', 'pm', 'contributor', 'supplier', 'reader'];
export const PERMISSIONS = [
    'project.read',
    'project.manageMembers',
    /** Create/rename/move folders and shared sheets in the project tree. */
    'content.manage',
    /** Publish a private My Workspace sheet to the project. */
    'grid.publish',
    'grid.editSchema',
    'grid.editCells',
    'grid.addRows',
    'grid.deleteRows',
    'grid.appendUpdate',
    'action.update',
    /** §13: move an item's status / record a board review. */
    'governance.record',
    /** §13: create and edit boards. */
    'governance.manageBoards',
    /** §13: edit this project's status workflows. */
    'governance.configure',
    /** §6.3/§6.4: see the Highlight Report and dashboards (they include finance, so not suppliers). */
    'report.read',
    /** §6.3: publish a dated Highlight Report snapshot (sets the project's RAG). */
    'report.publish',
    /** §6.4: add, edit and remove charts on the project dashboard. */
    'dashboard.edit',
    /** §17: set this project's logo / colours (overrides the tenant brand within the project). */
    'project.brand',
    /** §18: send a document for review (incl. external review links) and revoke links. */
    'document.review',
    /** §19 M9.3: create project/workstream chat channels and manage who is in them. */
    'chat.manageChannels',
    /** §27 M13.1: edit the master / workstream plans (tasks, dates, dependencies, baselines). A mini-plan's own
     *  owner may always edit their mini-plan. */
    'plan.edit',
];
const MATRIX = {
    owner: PERMISSIONS,
    pm: PERMISSIONS,
    contributor: [
        'project.read',
        'content.manage',
        'grid.publish',
        'grid.editCells',
        'grid.addRows',
        'grid.appendUpdate',
        'action.update',
        'governance.record',
        'report.read',
        'dashboard.edit',
    ],
    // Suppliers may post line-item updates on rows they can see, but not change the register itself.
    supplier: ['project.read', 'grid.appendUpdate'],
    reader: ['project.read', 'report.read'],
};
/** Data lifecycle: all that's left on an archived project — it's read-only, for everyone. */
export const ARCHIVED_PERMISSIONS = ['project.read', 'report.read'];
export function can(role, permission) {
    return !!role && MATRIX[role].includes(permission);
}
export const AFFILIATIONS = [
    'staff',
    'nhs',
    'agency_contractor',
    'freelance',
    'supplier',
    'stakeholder',
];
/**
 * §23 M12.2 capabilities: held by a person or a group (tenant admins hold them all). They ADD to what project roles
 * allow — in projects you're a member of — so the role defaults are unchanged. `author_courses` and `export_data` are
 * tenant-wide (learning authoring; exports when the tenant restricts them).
 */
export const CAPABILITIES = [
    'author_courses',
    'manage_dashboards',
    'run_meetings',
    'manage_workflows',
    'manage_branding',
    'export_data',
    'manage_members',
    'manage_features',
];
export const CAPABILITY_PERMISSIONS = {
    author_courses: [],
    manage_dashboards: ['dashboard.edit', 'report.publish'],
    run_meetings: ['governance.manageBoards', 'governance.record'],
    manage_workflows: ['governance.configure'],
    manage_branding: ['project.brand'],
    export_data: [],
    manage_members: ['project.manageMembers', 'chat.manageChannels'],
    // §35 organisation-wide: Administration → Features (switch the flags Ibexstone delegated)
    manage_features: [],
};
/** A project permission from the role, or from a capability (only for members: a capability isn't access). */
export function canWith(role, permission, capabilities) {
    return (can(role, permission) ||
        (!!role && capabilities.some((c) => CAPABILITY_PERMISSIONS[c].includes(permission))));
}
/**
 * §23 M12.3 features Ibexstone can switch off per tenant (platform console). Everything is on unless switched off;
 * off hides it in the app and the api refuses it.
 */
// §32 M16: 'ai' (summarise / compare / generate / ask / the command agent) and 'voice' (speech to text) — a tenant
// still has to switch them on and choose a provider; these let Ibexstone withhold them per tenant / tier.
// M18: 'connected' — connected folders (SharePoint / OneDrive / local folders surfaced in projects).
// #151: 'office' — the in-app Office editor (Word / Excel / PowerPoint through Collabora; needs it deployed too).
export const FEATURES = [
    'learning',
    'chat',
    'calls',
    'diagrams',
    'ai',
    'voice',
    'connected',
    'office',
];
//# sourceMappingURL=rbac.js.map